import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { BroadcastComposeModal } from '../components/domain/BroadcastComposeModal.js';
import { BroadcastDetailModal } from '../components/domain/BroadcastDetailModal.js';

interface Broadcast {
  id: string;
  templateKey: string | null;
  templateLabel: string | null;
  messageBody: string;
  sentAt: string;
  sentBy: string | null;
  audienceSummary: string | null;
  recipientCount: number;
}

function relTime(d: Date): string {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export function Broadcasts() {
  const { canBroadcast } = useRolePerms();
  const [composeOpen, setComposeOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data: broadcasts = [] } = useQuery({
    queryKey: ['broadcasts'],
    queryFn: () => api.get<Broadcast[]>('/broadcasts').then((r) => r.data),
  });

  const total = broadcasts.length;
  const last7d = broadcasts.filter((b) => Date.now() - new Date(b.sentAt).getTime() < 7 * 86_400_000).length;
  const last30d = broadcasts.filter((b) => Date.now() - new Date(b.sentAt).getTime() < 30 * 86_400_000).length;
  const totalRecipients = broadcasts.reduce((a, b) => a + b.recipientCount, 0);

  const kpiTiles = [
    { label: 'Total Broadcasts', val: total, sub: 'all time', color: '#3B82F6' },
    { label: 'Last 7 Days', val: last7d, sub: 'recent activity', color: '#22C55E' },
    { label: 'Last 30 Days', val: last30d, sub: 'monthly volume', color: '#E8A427' },
    { label: 'Total Recipients', val: totalRecipients, sub: 'workers reached', color: '#A855F7' },
  ];

  return (
    <div className="page" id="page-broadcasts">
      <div className="hbar">
        <div>
          <div className="ph">📢 Broadcast History</div>
          <div className="ps">Messages sent to groups of workers. Click any record for details.</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {canBroadcast ? <button className="btn btn-ghost btn-sm" onClick={() => setComposeOpen(true)}>+ New Broadcast</button> : null}
        </div>
      </div>
      <div className="g4" style={{ marginBottom: 16 }} id="broadcast-kpis">
        {kpiTiles.map((k) => (
          <div key={k.label} className="kpi" style={{ borderTopColor: k.color }}>
            <div className="lbl">{k.label}</div>
            <div className="val" style={{ color: k.color }}>{k.val}</div>
            <div className="sub" style={{ color: k.color, opacity: .7 }}>{k.sub}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div className="sec-title" style={{ marginBottom: 10 }}>All Broadcasts</div>
        <div id="broadcast-list">
          {broadcasts.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 20 }}>No broadcasts sent yet.</div>
          ) : (
            broadcasts.map((b) => (
              <div key={b.id} onClick={() => setDetailId(b.id)} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(30,42,64,.4)', cursor: 'pointer' }}>
                <div style={{ fontSize: 20 }}>📢</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12 }}>{b.templateLabel ?? 'Custom message'}</div>
                    <span className="pill" style={{ fontSize: 9 }}>{b.recipientCount} recipient{b.recipientCount !== 1 ? 's' : ''}</span>
                    {b.audienceSummary ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>{b.audienceSummary}</span> : null}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--faint)', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{(b.messageBody ?? '').slice(0, 180)}{(b.messageBody ?? '').length > 180 ? '…' : ''}</div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {relTime(new Date(b.sentAt))}
                  {b.sentBy ? <div style={{ fontSize: 9, marginTop: 2 }}>by {b.sentBy}</div> : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {composeOpen ? <BroadcastComposeModal onClose={() => setComposeOpen(false)} /> : null}
      {detailId ? <BroadcastDetailModal broadcastId={detailId} onClose={() => setDetailId(null)} /> : null}
    </div>
  );
}
